import z from "zod";

function toDate(value: unknown) {
  // Firestore Timestamp (admin + web SDK)
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof value.toDate === "function"
  ) {
    return value.toDate() as Date;
  }

  // Native Date
  if (value instanceof Date) {
    return value;
  }

  // Serialized Date
  if (typeof value === "string") {
    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) {
      return null;
    }
    return date;
  }

  return null;
}

export const FirestoreDateSchema = z.preprocess((val) => toDate(val), z.date());
export const FirestoreNullableDateSchema = z.preprocess(
  (val) => toDate(val),
  z.date().or(z.null()),
);
export type FirestoreDate = z.infer<typeof FirestoreDateSchema>;

export const OrderTypeSchema = z.enum(["payment", "subscription"]);
export type OrderType = z.infer<typeof OrderTypeSchema>;
export const PaymentOrderTypeSchema = z.literal("payment");
export type PaymentOrderTypeSchema = z.infer<typeof PaymentOrderTypeSchema>;
export const PaymentOrderStatusSchema = z.enum([
  "created",
  "processing",
  "succeeded",
  "failed",
  "refunded",
  "disputed",
  "requires_manual_approval",
]);
export type PaymentOrderStatus = z.infer<typeof PaymentOrderStatusSchema>;

export const SubscriptionOrderTypeSchema = z.literal("subscription");
export type SubscriptionOrderTypeSchema = z.infer<
  typeof PaymentOrderTypeSchema
>;
export const SubscriptionOrderStatusSchema = z.enum([
  "created",
  "processing",
  "succeeded",
  "failed",
]);
export type SubscriptionOrderStatus = z.infer<
  typeof SubscriptionOrderStatusSchema
>;
export const PaymentOrderSchema = z.object({
  id: z.string(),
  created_at: FirestoreDateSchema,
  last_updated_at: FirestoreDateSchema,
  type: PaymentOrderTypeSchema,
  amount: z.number().positive().optional(),
  status: PaymentOrderStatusSchema,
  status_override: PaymentOrderStatusSchema.optional(),
  stripe_checkout_session_id: z.string().optional(),
  stripe_payment_intent_id: z.string().optional(),
});
export type PaymentOrder = z.infer<typeof PaymentOrderSchema>;
export const SubscriptionOrderSchema = z.object({
  id: z.string(),
  created_at: FirestoreDateSchema,
  last_updated_at: FirestoreDateSchema,
  type: SubscriptionOrderTypeSchema,
  amount: z.number().positive().optional(),
  status: SubscriptionOrderStatusSchema,
  status_override: SubscriptionOrderStatusSchema.optional(),
  stripe_checkout_session_id: z.string().optional(),
  stripe_subscription_id: z.string().optional(),
});
export type SubscriptionOrder = z.infer<typeof SubscriptionOrderSchema>;
export const OrderSchema = z.discriminatedUnion("type", [
  PaymentOrderSchema,
  SubscriptionOrderSchema,
]);
export type Order = z.infer<typeof OrderSchema>;
export const PaymentOrderPublicSchema = PaymentOrderSchema.pick({
  created_at: true,
  id: true,
  type: true,
  amount: true,
  status: true,
  status_override: true,
});
export type PaymentOrderPublic = z.infer<typeof PaymentOrderPublicSchema>;
export const SubscriptionOrderPublicSchema = SubscriptionOrderSchema.pick({
  created_at: true,
  id: true,
  type: true,
  amount: true,
  status: true,
  status_override: true,
});
export type SubscriptionOrderPublic = z.infer<
  typeof SubscriptionOrderPublicSchema
>;
export const OrderPublicSchema = z.discriminatedUnion("type", [
  PaymentOrderPublicSchema,
  SubscriptionOrderPublicSchema,
]);
export type OrderPublic = z.infer<typeof OrderPublicSchema>;

export const UserEntitlementsSchema = z.object({ plugin: z.boolean() });
export type UserEntitlements = z.infer<typeof UserEntitlementsSchema>;
export const UserPublicSchema = z.object({
  newsletter_opt_in: z.boolean().optional(),
  onboarding_level: z.int().min(0).max(1).optional(),
  entitlements: UserEntitlementsSchema.optional(),
});
export type UserPublic = z.infer<typeof UserPublicSchema>;
export const UserDeviceSchema = z.object({ deviceId: z.string() });
export const UserActivationSchema = z.object({
  device: UserDeviceSchema,
  issued_at: FirestoreDateSchema,
  valid_until: FirestoreDateSchema,
  refreshed_at: FirestoreDateSchema.optional(),
});
export type UserActivation = z.infer<typeof UserActivationSchema>;
export const UserInternalSchema = z.object({
  stripe_customer_id: z.string().optional(),
  activations: UserActivationSchema.array().optional(),
  deleted: z.boolean().optional(),
});
export type UserInternal = z.infer<typeof UserInternalSchema>;
export const UserSchema = UserPublicSchema.and(UserInternalSchema);
export type User = z.infer<typeof UserSchema>;
